import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Capacitor } from "@capacitor/core";
import { isInstalledPWA, wasPWAEverInstalled } from "@/utils/pwa";

/**
 * After a successful Stripe checkout the redirect lands on
 * /dashboard?success=true. If the user paid inside a browser tab but the
 * PWA is already installed on this device, show a one-time interstitial
 * asking them to continue in the installed app so their session, push
 * subscription, and Google Calendar link live in one place.
 */
export const PostCheckoutSurfaceGuard: React.FC = () => {
  const location = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!location.pathname.startsWith("/dashboard")) return;
    const params = new URLSearchParams(location.search);
    if (params.get("success") !== "true") return;
    if (isInstalledPWA()) return;
    if (!wasPWAEverInstalled()) return;
    setShow(true);
  }, [location.pathname, location.search]);

  if (!show) return null;

  const handleOpen = () => {
    window.location.href = "https://lupuslogix.com/dashboard?source=pwa&success=true";
  };

  const handleStay = () => setShow(false);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm rounded-lg bg-background p-5 shadow-xl">
        <div className="mb-3 flex items-center gap-2">
          <div className="rounded-full bg-primary/10 p-2">
            <Smartphone className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Payment successful 🎉</h2>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          LupusLogix is already installed on this device. Open the installed
          app to continue — reminders, push notifications, and Google Calendar
          sync live there.
        </p>
        <div className="flex flex-col gap-2">
          <Button onClick={handleOpen} className="w-full">Open Installed App</Button>
          <Button variant="ghost" onClick={handleStay} className="w-full">
            Continue in Browser
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PostCheckoutSurfaceGuard;